import React, { useEffect, useState, useCallback } from "react";
import { Row, Col, Typography, Spin, Card } from "antd";
import axios from "axios";
import { API_URL } from "../../config";
import { DeviceIds } from "../common/helper/helper";
import { useTranslation } from "react-i18next";
import { ref, onValue } from "firebase/database";
import { database } from "../../firebaseConfig";
const { Title } = Typography;

const SummaryTab = ({ appointment_id, patientDetails }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ecgImageUrl, setEcgImageUrl] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchEcgSummary = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/device/ecg_summary?appointment_id=${appointment_id}`,
          {
            responseType: "blob",
          },
        );

        const imageUrl = URL.createObjectURL(response.data);
        setEcgImageUrl(imageUrl);
      } catch (error) {
        console.error("Error fetching ECG summary:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEcgSummary();

    return () => {
      if (ecgImageUrl) {
        URL.revokeObjectURL(ecgImageUrl);
      }
    };
  }, [appointment_id, ecgImageUrl]);

  const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    let response;

    if (patientDetails?.kiosk_id) {
      response = await axios.get(
        `${API_URL}/device/appointment_summary/?appointment_id=${appointment_id}`,
      );
      setSummaryData(response.data || []);
    } else if (patientDetails?.tablet_id) {
      response = await axios.get(
        `${API_URL}/device/readings/v2/?patient_id=${patientDetails.patient_id}`,
      );
      setSummaryData(response.data[0]?.readings || []);
    } else {
      setSummaryData([]); 
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      setSummaryData([]);
    } else {
      setError(error);
    }
  } finally {
    setLoading(false);
  }
}, [appointment_id, patientDetails?.patient_id, patientDetails?.kiosk_id, patientDetails?.tablet_id]);

  useEffect(() => {
    const appointmentRef = ref(database, `appointments/${appointment_id}`);
    const unsubscribe = onValue(appointmentRef, (snapshot) => {
      const data = snapshot.val();
      if (data?.last_updated && data.last_updated !== lastUpdated) {
        setLastUpdated(data.last_updated);
        fetchData();
      }
    });

    fetchData();

    return () => unsubscribe();
  }, [appointment_id, fetchData, lastUpdated]);

  if (loading) return <Spin />;
  if (error) return <p>Error loading data</p>;

  const getDeviceValue = (deviceId) => {
    const device = summaryData.find((item) => item.device_id === deviceId);
    return device
      ? { value: device.string_value || device.value, unit: device.units || "" }
      : { value: "-", unit: "" };
  };

  const baseVitalsLeft = [
    {
      label: t("vitalSigns.bloodPressure"),
      value: `${getDeviceValue(DeviceIds.BPSys).value}/${getDeviceValue(DeviceIds.BPDia).value}`,
      unit: getDeviceValue(DeviceIds.BPDia).unit,
    },
    {
      label: t("vitalSigns.temperature"),
      ...getDeviceValue(DeviceIds.Temperature),
      isHigh: parseFloat(getDeviceValue(DeviceIds.Temperature).value) > 37.5,
    },
  ];

  const baseVitalsRight = [
    {
      label: t("vitalSigns.spo2"),
      ...getDeviceValue(DeviceIds.SPO2),
    },
  ];

  let vitalsLeft = [...baseVitalsLeft];
  let vitalsRight = [...baseVitalsRight];

  if (patientDetails?.kiosk_id) {
    vitalsLeft.push({
      label: t("vitalSigns.bodyFat"),
      ...getDeviceValue(DeviceIds.FatPercentage),
    });

    vitalsRight.push(
      {
        label: t("vitalSigns.weight"),
        ...getDeviceValue(DeviceIds.Weight),
      },
      {
        label: t("vitalSigns.height"),
        ...getDeviceValue(DeviceIds.Height),
      },
    );
  } else if (patientDetails?.tablet_id) {
    vitalsLeft.push(
      {
        label: "Cholesterol",
        ...getDeviceValue(DeviceIds.Cholesterol),
      },
      {
        label: "HbA1C",
        ...getDeviceValue(DeviceIds.NGSP),
      },
    );

    vitalsRight.push(
      {
        label: "Pulse Rate",
        ...getDeviceValue(DeviceIds.SPO2Hr),
      },
      {
        label: "Triglycerides",
        ...getDeviceValue(DeviceIds.Triglycerides),
      },
      {
        label: `HDL/LDL`,
        value: `${getDeviceValue(DeviceIds.HDL).value}/${getDeviceValue(DeviceIds.LDL).value}`,
        unit: getDeviceValue(DeviceIds.HDL).unit,
      },
    );
  }

  return (
    <React.Fragment>
      {!patientDetails?.mobile ? (
        <Card>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Row>
                <Col span={12}>
                  <Title level={5}>{t('device.vital')}</Title>
                </Col>
                <Col span={12}>
                  <Title level={5}>{t('device.reading')}</Title>
                </Col>
              </Row>
              {vitalsLeft.map((vital) => (
                <Row key={vital.label} style={{ marginBottom: "8px" }}>
                  <Col span={12} style={{ fontWeight: "bold" }}>
                    {vital.label}
                  </Col>
                  <Col
                    span={12}
                    style={{ color: vital.isHigh ? "red" : "black" }}
                  >
                    {vital.value} {vital.unit}
                  </Col>
                </Row>
              ))}
            </Col>
            <Col span={12}>
              <Row>
                <Col span={12}>
                  <Title level={5}>{t('device.vital')}</Title>
                </Col>
                <Col span={12}>
                  <Title level={5}>{t('device.reading')}</Title>
                </Col>
              </Row>
              {vitalsRight.map((vital) => (
                <Row key={vital.label} style={{ marginBottom: "8px" }}>
                  <Col span={12} style={{ fontWeight: "bold" }}>
                    {vital.label}
                  </Col>
                  <Col span={12}>
                    {vital.value} {vital.unit}
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        </Card>
      ) : null}
      <Card>
        <Col xs={24} md={24}>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
              }}
            >
              <Spin size="large" />
            </div>
          ) : ecgImageUrl ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
                width: "500px",
                margin: "auto",
              }}
            >
              <img
                alt="ECG Report"
                src={ecgImageUrl}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                  console.error("Failed to load image");
                }}
              />
            </div>
          ) : (
            <div> {t('message.noECGDataAvailable')}</div>
          )}
        </Col>
      </Card>
    </React.Fragment>
  );
};

export default SummaryTab;
